import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Form, InputGroup, Badge, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import { FaSearch, FaFilter, FaTruck, FaClock, FaEye, FaCopy } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import orderService from '../services/orderService';

function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const itemsPerPage = 10;

  const truncateTransactionId = (txId) => {
    if (!txId || txId.length <= 12) return txId;
    return `${txId.substring(0, 5)}...${txId.substring(txId.length - 5)}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: 'created_at',
        sort_order: 'desc',
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const data = await orderService.getAll(params);
      setOrders(data.orders);
      setTotalOrders(data.total);
      setTotalPages(data.pages);

      // Fetch counts for stats cards
      const pendingData = await orderService.getAll({ status: 'pending', per_page: 1 });
      const completedData = await orderService.getAll({ status: 'completed', per_page: 1 });
      setPendingCount(pendingData.total);
      setCompletedCount(completedData.total);
    } catch (err) {
      setError('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fulfillmentRate = totalOrders > 0 ? Math.round((completedCount / totalOrders) * 100) : 0;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusVariant = (status) => {
    const variants = {
      completed: 'success',
      pending: 'warning',
      canceled: 'danger'
    };
    return variants[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Orders"
        subtitle={`${totalOrders} orders • ${pendingCount} pending`}
      />

      <Row className="g-3 mb-4">
        <Col md={8}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search orders by ID or User ID..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaFilter className="text-muted" />
            </InputGroup.Text>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="canceled">Canceled</option>
            </Form.Select>
          </InputGroup>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-primary bg-opacity-10 rounded me-3">
                  <FaTruck className="text-primary" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Fulfillment Rate</p>
                  <h4 className="fw-bold mb-0">{fulfillmentRate}%</h4>
                  <small className="text-muted">{completedCount} of {totalOrders} orders completed</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-warning bg-opacity-10 rounded me-3">
                  <FaClock className="text-warning" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Orders Pending</p>
                  <h4 className="fw-bold mb-0">{pendingCount}</h4>
                  <small className="text-muted">Awaiting confirmation or stock</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="fw-semibold">ID</th>
                  <th className="fw-semibold">Customer</th>
                  <th className="fw-semibold">Transaction ID</th>
                  <th className="fw-semibold">Payment Type</th>
                  <th className="fw-semibold">Status</th>
                  <th className="fw-semibold text-end">Total</th>
                  <th className="fw-semibold">Date</th>
                  <th className="fw-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>#{order.id}</td>
                    <td>
                      {order.user ? (
                        <div>
                          <div className="fw-semibold">{order.user.username}</div>
                          <small className="text-muted">{order.user.email}</small>
                        </div>
                      ) : (
                        <span className="text-muted">User ID: {order.user_id}</span>
                      )}
                    </td>
                    <td>
                      {order.payment ? (
                        <div className="d-flex align-items-center gap-1">
                          <small className="text-muted">{truncateTransactionId(order.payment.transaction_id)}</small>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-muted"
                            onClick={() => copyToClipboard(order.payment.transaction_id)}
                            title="Copy transaction ID"
                          >
                            <FaCopy size={12} />
                          </Button>
                        </div>
                      ) : (
                        <small className="text-muted">-</small>
                      )}
                    </td>
                    <td>
                      {order.payment ? (
                        <Badge bg="secondary" className="text-dark">
                          {order.payment.method_display}
                        </Badge>
                      ) : (
                        <small className="text-muted">-</small>
                      )}
                    </td>
                    <td>
                      <Badge bg={getStatusVariant(order.status)}>
                        {order.display_status}
                      </Badge>
                    </td>
                    <td className="text-end">${order.display_total}</td>
                    <td><small className="text-muted">{new Date(order.created_at).toLocaleDateString()}</small></td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        <FaEye className="me-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </Container>
  );
}

export default OrdersPage;
